// @ts-strict
import { useCallback, useEffect, useRef, useState } from "react";

interface UseCopyToClipboard {
  copied: boolean;
  copy: (text: string) => void;
}

/**
 * Canonical clipboard-copy hook with transient "copied" feedback.
 *
 * `copy(text)` writes `text` to the clipboard, flips `copied` to true, and
 * resets it to false after `resetMs`. Calling `copy` again restarts the timer,
 * and the timer is cleared on unmount. Use this anywhere you need copy-to-
 * clipboard behavior; pair it with `<CopyButton>` for the standard button UI.
 */
const useCopyToClipboard = (resetMs = 2000): UseCopyToClipboard => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const copy = useCallback(
    (text: string) => {
      // navigator.clipboard is undefined in insecure (non-HTTPS) contexts and
      // some embedded iframes; bail out rather than throwing in the handler.
      if (!navigator.clipboard) {
        return;
      }
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => setCopied(false), resetMs);
        })
        .catch(() => {
          // Write rejected (e.g. permissions denied); leave copied false.
        });
    },
    [resetMs]
  );

  return { copied, copy };
};

export default useCopyToClipboard;
